import moment from 'moment'
import { isNil, pick, equals } from 'ramda'
import {
  BOOKING_TYPE_RESERVATION,
  FORMAT,
  RESOURCE_TYPE_STAFFER as STAFFER,
  RESOURCE_TYPE_APPLIANCE as APPLIANCE,
  CUSTOMER_MODES,
  SERVICE_VARIANT_MODE,
  RESOURCE_TYPE_STAFFER,
  RESOURCE_TYPE_APPLIANCE,
  PRICE_TYPES,
} from '@/lib/consts.js'

import { APPOINTMENT_EVENTS, RESOURCES_AVAILABILITY_TYPES } from './consts.js'

import { TRANSACTION_STATUSES } from '@/components/sales/checkout/consts.js'

import { REPEATING_UPDATE_FUTURE_ACTION, REPEAT_TYPES } from './repeating/consts.js'
import { appointmentCalendarEvent } from '@/plugins/event-bus.js'

import { COMBO_TYPE } from '@/components/settings/combos/const.js'
import { useFilters } from '@/composables/filters/index.js'

export const cardId = id => `cc-${id}`

export function shouldAskAboutUpdateFuture ({ editedAppointment, currentTime, originalAppointment, action }) {
  return (
    editedAppointment
    && editedAppointment.repeating
    && editedAppointment.repeating.repeat !== REPEAT_TYPES.CUSTOM
    && !isGroupBookingAppointment(editedAppointment)
    && (
      // only ask if it's not the last one in the series
      (!editedAppointment.repeating.ending_on
      || !moment(editedAppointment.repeating.ending_on)
        .isSame(moment(editedAppointment.booked_from), 'day'))
    ) && (
      // only ask if it's future booking
      moment(editedAppointment.booked_till).toDate() > moment(currentTime || undefined)
    )
  ) || shouldAskAboutUpdateGroup({ editedAppointment, originalAppointment, action })
}

export function shouldAskAboutUpdateGroup ({ editedAppointment, originalAppointment, action }) {
  return (
    isExistingGroupBookingAppointment(editedAppointment) && (
      // only ask if first subbooking was rescheduled (for update)
      (action === REPEATING_UPDATE_FUTURE_ACTION.UPDATE
      && !equals(
        pick(['booked_from'], originalAppointment || {}),
        pick(['booked_from'], editedAppointment)
      )) || action === REPEATING_UPDATE_FUTURE_ACTION.CANCEL
    )
  )
}

export function calculateAvailability ({ resource, availability, $t }) {
  const id = resource && resource.id
  const data = (availability || {})[id] || {}
  return data
}

export function filterEmptyResourceList (resourceList) {
  return resourceList.filter(id => (id !== -1 && id !== null)).length === 0
}

export function waitTimeToDuration (waitTime) {
  waitTime = waitTime || {}
  const minutes = 60 * (waitTime.hours || 0) + (waitTime.minutes || 0)
  return moment.duration(minutes, 'minutes')
}

export function waitTimeLabel (subbooking) {
  const filters = useFilters()
  if (waitTimeToDuration(subbooking.wait_time).asMinutes() < 0) {
    return ['components.appointment.overlapping']
  }

  if (subbooking.wait_type && subbooking.wait_type === 'gap_error') {
    return ['components.appointment.wait-time-gap-error']
  }

  const key = `components.appointment.wait-time`

  switch (subbooking.wait_type) {
    case 'gap':
      return [`${key}-gap`, {
        time: filters.formatRelativeDelta(subbooking.gap_time),
      }]
    case 'gap_hole':
      return [`${key}-gap-hole`, {
        time: filters.formatRelativeDelta(subbooking.gap_time),
      }]
    case 'gap_included':
      return [`${key}-gap-included`, {
        wtime: filters.formatRelativeDelta(subbooking.wait_time),
        gtime: filters.formatRelativeDelta(subbooking.gap_time),
      }]
    default:
      return (subbooking.wait_type && (Object.keys(subbooking.wait_time).length !== 0 && subbooking.wait_time.constructor === Object))
        ? [`${key}`, { time: filters.formatRelativeDelta(subbooking.wait_time) }]
        : [`${key}`, { time: filters.formatRelativeDelta({ minutes: 0 }) }]
  }
}

export function checkIfNoVariantSubbooking (serviceVariant) {
  return serviceVariant.mode === SERVICE_VARIANT_MODE.NO_VARIANT
}

export function subbookingValidation ({
  service_variant,
  booked_from,
  booked_till,
  staffer_id,
  appliance_id,
  combo_children,
}) {
  // validate service_variant
  const service = !service_variant
    || !service_variant.mode
    || (checkIfNoVariantSubbooking(service_variant) && !service_variant.service_name)
    || (service_variant.mode === SERVICE_VARIANT_MODE.VARIANT && !service_variant.id)

  // validate dates
  const dates = (!booked_from || !booked_till)

  // validate if same days
  const sameDays = booked_from && booked_till && !moment(booked_from).isSame(moment(booked_till), 'day')

  // validate resources
  let resources
  if (combo_children.length) {
    resources = combo_children.every(({ staffer_id, appliance_id }) => (staffer_id === null && appliance_id === null))
  } else {
    resources = staffer_id === undefined || appliance_id === undefined
     || (staffer_id === null && appliance_id === null)
  }

  return !(service || dates || sameDays || resources)
}

export function reservationValidation ({ booked_from, booked_till, staffer_id, appliance_id }) {
  // validate dates
  const dates = !booked_from || !booked_till

  // validate resources
  const resources = (staffer_id === undefined || staffer_id === null) && (appliance_id === undefined || appliance_id === null)
  return !(dates || resources)
}

/* Customer helpers */
export function generateCustomerCard (customer, walkinMode = true) {
  if (customer?._id || customer?.id) {
    return {
      mode: CUSTOMER_MODES.CARD,
      id: customer._id || customer.id,
    }
  }

  if (walkinMode) {
    if (customer && !customer?.email && !customer?.phone) {
      return {
        mode: CUSTOMER_MODES.MANUAL,
        ...customer,
      }
    } else {
      return {
        mode: CUSTOMER_MODES.WALKIN,
      }
    }
  }
  return null
}

export function customerHasContact (customer) {
  return !!(customer && (customer.merged_data.email || customer.merged_data.cell_phone))
}
export function customerHasCellPhone (customer) {
  return !!(customer && (customer.merged_data.cell_phone || customer.business_customer?.cell_phone))
}
export function customerHasEmail (customer) {
  return !!(customer && (customer.merged_data.email || customer.business_customer?.email))
}

export function convertReservationToAppointment (reservation) {
  const staffer = reservation.resources
    ? reservation.resources.find(res => res.type === STAFFER)
    : reservation.staffer
  const appliance = reservation.resources
    ? reservation.resources.find(res => res.type === APPLIANCE)
    : reservation.appliance

  return {
    '_version': reservation._version,
    'customer': {
      'mode': 'walk-in',
    },
    'subbookings': [
      {
        '_version': reservation._version,
        'booked_from': reservation.reserved_from,
        'booked_till': reservation.reserved_till,
        'appliance': appliance,
        'appliance_id': appliance && appliance.id,
        'staffer': staffer,
        'staffer_id': staffer && staffer.id,
        'service': null,
        'service_variant': {
          'service_name': null,
          'mode': 'no-variant',
        },
        'id': reservation.id,
        'autoassign': false,
        'type': reservation.type,
      },
    ],
    'appointment_id': reservation.id,
    'appointment_type': 'single',
    'booked_from': reservation.reserved_from,
    'booked_till': reservation.reserved_till,
    'type': reservation.type,
    'actions': {
      'cancel': true,
      'cancel_no_show': false,
      'change': true,
      'change_time_or_note': true,
    },
    'reason': reservation.reason,
    'traveling': null,
  }
}

export function convertAppointmentToReservationObj (model) {
  const subbooking = model.subbookings[0]
  const resources = []

  if (subbooking.staffer_id) {
    resources.push(subbooking.staffer_id)
  }
  if (subbooking.appliance_id) {
    resources.push(subbooking.appliance_id)
  }

  return {
    id: subbooking.id,
    reserved_from: subbooking.booked_from,
    reserved_till: subbooking.booked_till,
    resources,
    reason: model.reason,
    overbooking: false,
  }
}

export function getNewAppointmentStart (time, openHours) {
  const start = (moment.isMoment(time) && time) || moment(time || undefined)
  const day = start.day()
  const openFromTills = openHours.filter(h => h.day_of_week === day)
  for (let openFromTill of openFromTills) {
    const openFrom = getBusinessHour(openFromTill.hour_from, start)
    const openTill = getBusinessHour(openFromTill.hour_till, start)
    if (start.isSameOrBefore(openFrom)) {
      return openFrom
    }
    if (start.isBefore(openTill)) {
      return adjustStart(start)
    }
  }

  // no openHours data or currentTime after openTill
  const nextDay = (day === 6) ? 0 : day + 1
  const nextOpenFromTill = openHours.find(h => h.day_of_week === nextDay)
  if (nextOpenFromTill) {
    return getBusinessHour(nextOpenFromTill.hour_from, start.clone().add(1, 'days'))
  }

  return adjustStart(start)
}

export function getBusinessHour (hourText, date) {
  const time = hourText.split(':').map(Number)
  return date.clone().hour(time[0]).minute(time[1])
}

/* port of booksy-api/lib/tools.py: nearest_5_minutes(dt) */
export function adjustStart (date, interval = 15) {
  const diff = moment.duration({
    minutes: date.minute() % interval,
    seconds: date.seconds(),
    miliseconds: date.milliseconds(),
  })
  return (diff.asMilliseconds() > 0)
    ? date.clone().subtract(diff).add(interval, 'minutes')
    : date
}

export function mapBookingCustomer (model) {
  const customer = {
    id: null,
    name: null,
  }

  if (!model) {
    return customer
  }

  const { merged_data } = model

  if (merged_data) {
    customer.id = merged_data.id
    customer.name = merged_data.full_name || merged_data.cell_phone || merged_data.email
  }

  return customer
}

export function createSubbookingObject ({
  day,
  booked_from,
  booked_till,
  staffer,
  appliance,
  serviceData,
  addons,
  combo_children,
}, businessOpenHours, currentTime) {
  let start

  if (booked_from) {
    start = moment.isMoment(booked_from) ? booked_from : moment(booked_from)
  } else if (currentTime && day && day.isSame(currentTime, 'day')) {
    start = getNewAppointmentStart(currentTime, businessOpenHours)
  } else if (day) {
    start = getNewAppointmentStart(day, businessOpenHours)
  } else {
    start = getNewAppointmentStart(moment(), businessOpenHours)
  }

  const duration = serviceData && serviceData.service?.variant?.duration
    ? serviceData.service.variant.duration
    : 30
  const end = booked_till ? moment(booked_till) : start.clone().add(duration, 'minutes')

  return {
    booked_from: start.format(FORMAT),
    booked_till: end.format(FORMAT),
    staffer: staffer !== undefined ? staffer : null,
    appliance: (appliance !== undefined) ? appliance : null,
    staffer_id: staffer !== undefined ? (staffer && staffer.id) : -1,
    autoassigned_staffer_id: staffer?.autoassign ? (staffer && staffer.id) : null,
    appliance_id: (appliance !== undefined) ? (appliance && appliance.id) : null,
    service: (serviceData && serviceData.service) || {},
    service_variant: (serviceData
      && (
        serviceData.service_variant
        || (serviceData.service && serviceData.service.variant)
      )
    ) || null,
    addons,
    combo_children,
  }
}

export function createReservationObj ({ day, from, till, staffer, appliance }, businessOpenHours, currentTime) {
  let start

  if (from) {
    start = moment.isMoment(from) ? from : moment(from)
  } else if (currentTime && day && day.isSame(currentTime, 'day')) {
    start = getNewAppointmentStart(currentTime, businessOpenHours)
  } else if (day) {
    start = getNewAppointmentStart(day, businessOpenHours)
  } else {
    start = getNewAppointmentStart(moment(), businessOpenHours)
  }

  const duration = 30
  const end = till || start.clone().add(duration, 'minutes')

  const resources = []

  if (staffer) {
    resources.push(staffer)
  }

  if (appliance) {
    resources.push(appliance)
  }

  return {
    id: null,
    type: BOOKING_TYPE_RESERVATION,
    reserved_from: start.format(FORMAT),
    reserved_till: end.format(FORMAT),
    resources,
    reason: '',
  }
}

export function newOrEditTransaction (appointment) {
  return (
    appointment.payment_info
    && appointment.payment_info.transaction_info
    && TRANSACTION_STATUSES.includes(
      appointment.payment_info.transaction_info.status_type
    )
  )
}

export function checkIfTravelingObjectIsEmpty (travelingData) {
  const { price, formatted_price, ...traveling } = travelingData || {}

  return (
    isNil(travelingData)
    || Object.values(traveling).every(v => !v)
  )
}

export function appointmentChangeEvent (booking) {
  let applianceId = booking.appliance_id
  let stafferId = booking.staffer_id
  if (booking.combo_children?.length) {
    const { ccApplianceId, ccStafferId } = booking.combo_children.reduce((result, curr) => {
      if (!result.ccApplianceId) {
        result.ccApplianceId = curr.appliance_id
      }
      if (!result.ccStafferId) {
        result.ccStafferId = curr.staffer_id
      }

      return result
    }, { ccApplianceId: null, ccStafferId: null })

    applianceId = ccApplianceId
    stafferId = ccStafferId
  }

  appointmentCalendarEvent(APPOINTMENT_EVENTS.CHANGE,
    {
      bookingData: {
        from: booking.booked_from,
        staffer_id: stafferId,
        appliance_id: applianceId,
      },
      fromDrawer: true,
    }
  )
}

export function resourcesInSubbooking ({ staffer_id, appliance_id }) {
  return [
    {
      id: staffer_id,
      type: RESOURCE_TYPE_STAFFER,
    },
    {
      id: appliance_id,
      type: RESOURCE_TYPE_APPLIANCE,
    },
  ]
}

export function metaDataOfBooking ({
  index,
  isNew,
  model,
  multibooking,
  customer,
  resources,
  block,
  comboChildren = false,
  paid,
  ...rest
}) {
  return {
    ...rest,
    new: isNew,
    index,
    block,
    type: model.type,
    status: model.status,
    paid,
    customer,
    multibooking,
    has_note: !!model.customer_note || !!model.business_secret_note,
    from_promo: model.from_promo,
    resources,
    comboChildren,
  }
}

export function getComboChildrenByIdOrIndex (subbooking, { id, index }) {
  const { combo_children: comboChildren } = subbooking

  if (!comboChildren) {
    return
  }

  if (id) {
    return comboChildren.find(cc => cc.id === id)
  }

  return comboChildren[index]
}

export function adjustComboChildrenHours (
  comboChildren,
  index,
  { bookedFromDiff, bookedTillDiff },
  comboType
) {
  const isParallel = comboType === COMBO_TYPE.PARALLEL

  if (bookedFromDiff || (!isParallel && bookedTillDiff)) {
    const startIndex = bookedFromDiff ? 0 : index + 1

    for (let ccIndex = startIndex; ccIndex < comboChildren.length; ccIndex++) {
      if (index !== ccIndex) {
        const cc = comboChildren[ccIndex]
        const { bookedFrom, bookedTill } = changeBookingHours({
          bookedFrom: cc.booked_from,
          bookedTill: cc.booked_till,
        }, bookedFromDiff || bookedTillDiff)

        cc.booked_from = bookedFrom
        cc.booked_till = bookedTill
      }
    }
  }

  return comboChildren
}

function changeBookingHours ({ bookedFrom, bookedTill }, diffMinutes) {
  bookedFrom = moment(bookedFrom).add(diffMinutes, 'minutes').format(FORMAT)
  bookedTill = moment(bookedTill).add(diffMinutes, 'minutes').format(FORMAT)
  return { bookedFrom, bookedTill }
}

export function hasStafferOrResourceAnyError (sb) {
  const staffer = sb?._availability?.staffers[sb?.staffer_id]
  const appliance = sb?._availability?.appliances[sb?.appliance_id]

  return (
    staffer?.ok === false
      && staffer?.type === RESOURCES_AVAILABILITY_TYPES.ERROR
  )
    || (
      appliance?.ok === false
      && appliance.type === RESOURCES_AVAILABILITY_TYPES.ERROR
    )
}

export function isComboBooking (service) {
  // return true if has combo_type
  return !isNil(service?.combo_type)
}

export const mapAppointmentStaffer = (staffer) => {
  return staffer && pick(['id', 'type', 'description', 'name', 'photo_url', 'is_current_user', 'visible_on_calendar'])(staffer)
}

export const mapRequestAppointment = (appointment) => {
  if (appointment && appointment.appointment_uid) {
    return {
      ...appointment,
      subbookings: appointment.subbookings?.map(subbooking => {
        return {
          ...subbooking,
          autoassigned_staffer_id: subbooking.autoassign && subbooking.staffer_id && subbooking.staffer_id !== -1
            ? subbooking.staffer_id : null,
          combo_children: subbooking.combo_children?.map(comboChild => {
            return {
              ...comboChild,
              autoassigned_staffer_id: comboChild.autoassign && comboChild.staffer_id && comboChild.staffer_id !== -1
                ? comboChild.staffer_id : null,
            }
          }),
        }
      }),
    }
  }
  return appointment
}

export const isRepeatingAppointment = (appointment) => {
  return Boolean(appointment && (appointment.new_repeating || (appointment.appointment_uid && appointment.repeating)))
}

export const isGroupBookingAppointment = (appointment) => {
  return isRepeatingAppointment(appointment) && (appointment.new_repeating || appointment.repeating).repeat === REPEAT_TYPES.GROUP
}

export const isNewGroupBookingAppointment = (appointment) => {
  return isGroupBookingAppointment(appointment) && !appointment.repeating?.id
}

export const isExistingGroupBookingAppointment = (appointment) => {
  return isGroupBookingAppointment(appointment) && !isNewGroupBookingAppointment(appointment)
}

export const isTravelingPriceRequired = (priceType) => {
  return [PRICE_TYPES.FIXED, PRICE_TYPES.STARTS_AT].includes(priceType)
}

export const travelingPriceMinValue = (priceType) => {
  if (priceType === PRICE_TYPES.VARIES) {
    return -Number.MAX_VALUE
  }
  if (priceType === PRICE_TYPES.FREE) {
    return 0
  }
  return 0.01
}
